import React, { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import AdminRoute from "../components/AdminRoute";
import Layout from "../components/Layout";
import ITaxQuest from "../pages/ITaxQuest";

// Legacy `/formc/:engagementId[/import]` URLs are now unified under `/eci/…`.
// Preserved as redirects so old bookmarks / saved links keep working.
const FormcToEciRedirect: React.FC<{ suffix?: string }> = ({ suffix = "" }) => {
  const { engagementId } = useParams();
  return <Navigate to={`/eci/${engagementId ?? ""}${suffix}`} replace />;
};

// Lazy-loaded pages
const ITaxQuestA = lazy(() => import("../pages/ITaxQuestA"));
const Welcome = lazy(() => import("../pages/Welcome"));
const Login = lazy(() => import("../pages/Login"));
const ApproverLogin = lazy(() => import("../pages/ApproverLogin"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const Register = lazy(() => import("../pages/Register"));
const Preparer = lazy(() => import("../pages/Preparer"));
const Approver = lazy(() => import("../pages/Approver"));
const Feedback = lazy(() => import("../pages/Feedback"));
const FeedbackA = lazy(() => import("../pages/FeedbackA"));
const ECIForm = lazy(() => import("../pages/ECI/ECIForm"));
const ClientList = lazy(() => import("../pages/Clients/ClientList"));
const ClientCreateEdit = lazy(() => import("../pages/Clients/ClientCreateEdit"));
const EngagementList = lazy(() => import("../pages/Engagements/EngagementList"));
const EngagementCreateEdit = lazy(
  () => import("../pages/Engagements/EngagementCreateEdit")
);
const ImportEngagement = lazy(
  () => import("../pages/ECI/steps/ImportEngagement")
);
const NotFound = lazy(() => import("../pages/NotFound"));
const SuperAdmin = lazy(() => import("../pages/SuperAdmin"));
const AdminLayout = lazy(() => import("../pages/Admin/layout/AdminLayout"));
const AdminDashboardPage = lazy(() => import("../pages/Admin/pages/Dashboard"));
const AdminCsps = lazy(() => import("../pages/Admin/pages/Csps"));
const AdminCspDetail = lazy(() => import("../pages/Admin/pages/CSPDetail"));
const AdminUsers = lazy(() => import("../pages/Admin/pages/Users"));
const AdminSubscriptions = lazy(() => import("../pages/Admin/pages/Subscriptions"));
const AdminSettings = lazy(() => import("../pages/Admin/pages/Settings"));
const AdminProfile = lazy(() => import("../pages/Admin/pages/Profile"));

const routes: RouteObject[] = [
  {
    path: "/welcome",
    element: <Welcome />,
  },

  {
    path: "/login",
    element: <Login />,
  },

  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },

  {
    path: "/approver-login",
    element: <ApproverLogin />,
  },

  {
    path: "/register",
    element: <Register />,
  },

  /**
   * ===============================
   * SUPER ADMIN PANEL
   * ===============================
   */
  {
    path: "/super-admin",
    element: <SuperAdmin />,
  },
  {
    path: "/admin",
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    children: [
      { path: "", element: <Navigate to="/admin/dashboard" replace /> },
      { path: "dashboard", element: <AdminDashboardPage /> },
      { path: "csps", element: <AdminCsps /> },
      { path: "csps/:id", element: <AdminCspDetail /> },
      { path: "users", element: <AdminUsers /> },
      { path: "subscriptions", element: <AdminSubscriptions /> },
      { path: "settings", element: <AdminSettings /> },
      { path: "profile", element: <AdminProfile /> },
    ],
  },

  /**
   * ===============================
   * PREPARER DASHBOARD
   * ===============================
   */
  {
    path: "/preparer",
    element: (
      <ProtectedRoute>
        <Preparer />
      </ProtectedRoute>
    ),
  },

  {
    path: "/feedback",
    element: <Feedback />,
  },

  {
    path: "/feedback-approver",
    element: (
      <ProtectedRoute>
        <FeedbackA />
      </ProtectedRoute>
    ),
  },

  {
    path: "/approver",
    element: (
      <ProtectedRoute>
        <Approver />
      </ProtectedRoute>
    ),
  },

  {
    path: "/itaxquest",
    element: (
      <ProtectedRoute>
        <ITaxQuest />
      </ProtectedRoute>
    ),
  },

  {
    path: "/itaxquest-approver",
    element: (
      <ProtectedRoute>
        <ITaxQuestA />
      </ProtectedRoute>
    ),
  },

  /**
   * ===============================
   * ECI WORKSPACE (NO HARDCODING)
   * ===============================
   */

  // ❌ REMOVED: /eci → demo engagement redirect
  // Users must come here only with a real engagementId

  {
    path: "/eci/:engagementId",
    element: (
      <ProtectedRoute>
        <ECIForm />
      </ProtectedRoute>
    ),
  },

  /**
   * IMPORT JSON (CREATES + USES SAME ENGAGEMENT)
   */
  {
    path: "/eci/:engagementId/import",
    element: (
      <ProtectedRoute>
        <ImportEngagement />
      </ProtectedRoute>
    ),
  },

  {
    path: "/formc/:engagementId",
    element: <FormcToEciRedirect />,
  },

  {
    path: "/formc/:engagementId/import",
    element: <FormcToEciRedirect suffix="/import" />,
  },

  /**
   * ===============================
   * CLIENTS (CRUD)
   * ===============================
   */
  {
    path: "/clients",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <ClientList /> },
      { path: "create", element: <ClientCreateEdit /> },
      { path: ":id/edit", element: <ClientCreateEdit /> },
    ],
  },

  /**
   * ===============================
   * ENGAGEMENTS (CRUD)
   * ===============================
   */
  {
    path: "/engagements",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <EngagementList /> },
      { path: "create", element: <EngagementCreateEdit /> },
      { path: ":id/edit", element: <EngagementCreateEdit /> },
    ],
  },

  /**
   * ===============================
   * FALLBACK
   * ===============================
   */
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
